import { motion } from 'framer-motion';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'wouter';
import { 
  Users, PieChart as PieChartIcon, BarChart3, RefreshCw, 
  Filter, X, ChevronDown, ChevronRight, Copy, Check,
  Download, MapPin, Layers, Plus, Trash2
} from 'lucide-react';
import { 
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
  useAdminDemographics, 
  DemographicsFilter,
  serializeFilters,
  parseFilters,
  QuizFieldOption 
} from '../api/useAdminDemographics';
import { useAllQuizQuestions } from '@/data/quiz';
import { QuizQuestion } from '@shared/types';
import { Loader } from '@/components/Loader';

// Color palette
const COLORS = [
  '#8B5CF6', '#06B6D4', '#F59E0B', '#10B981', '#EF4444',
  '#EC4899', '#6366F1', '#84CC16', '#F97316', '#14B8A6',
  '#A855F7', '#3B82F6', '#22C55E', '#EAB308', '#F43F5E',
];

// Helper to get label for quiz value
function getQuizLabel(
  fieldId: string, 
  value: string, 
  quizQuestions: QuizQuestion[]
): string {
  // Handle special fields - values come back as display labels from server
  if (fieldId === 'isActiveSubscriber' || fieldId === 'hasBooking') {
    return value; // Server already returns 'Active Subscriber', 'Not Subscribed', etc.
  }
  
  const question = quizQuestions.find(q => q.id === fieldId);
  if (!question || !question.options) return value;
  
  const option = question.options.find(o => o.value === value);
  if (!option) return value;
  
  return option.emoji ? `${option.emoji} ${option.label}` : option.label;
}

// Special field options (non-quiz fields)
const SPECIAL_FIELD_OPTIONS: Record<string, { value: string; label: string }[]> = {
  isActiveSubscriber: [
    { value: 'true', label: 'Yes (Active Subscriber)' },
    { value: 'false', label: 'No (Not Subscribed)' },
  ],
  hasBooking: [
    { value: 'true', label: 'Yes (Has Booking)' },
    { value: 'false', label: 'No (No Booking)' },
  ],
};

// Get available options for a field
function getFieldOptions(fieldId: string, quizQuestions: QuizQuestion[]): { value: string; label: string }[] {
  // Handle special fields first
  if (SPECIAL_FIELD_OPTIONS[fieldId]) {
    return SPECIAL_FIELD_OPTIONS[fieldId];
  }
  
  const question = quizQuestions.find(q => q.id === fieldId);
  if (!question || !question.options) return [];
  
  return question.options.map(o => ({
    value: o.value,
    label: o.emoji ? `${o.emoji} ${o.label}` : o.label,
  }));
}

// Filter row component
function FilterRow({
  filter,
  quizFields,
  quizQuestions,
  onFieldChange,
  onValuesChange,
  onRemove,
}: {
  filter: DemographicsFilter;
  quizFields: QuizFieldOption[];
  quizQuestions: QuizQuestion[];
  onFieldChange: (field: string) => void;
  onValuesChange: (values: string[]) => void;
  onRemove: () => void;
}) {
  const fieldOptions = useMemo(() => {
    if (!filter.field) return [];
    return getFieldOptions(filter.field, quizQuestions);
  }, [filter.field, quizQuestions]);

  return (
    <div className="flex flex-wrap items-start gap-3 p-4 bg-dark-border/30 rounded-lg">
      <div className="flex flex-col gap-1.5 min-w-[180px]">
        <label className="text-xs text-dark-muted">Field</label>
        <select
          value={filter.field}
          onChange={(e) => onFieldChange(e.target.value)}
          className="bg-dark-border text-white px-3 py-2 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none text-sm"
        >
          <option value="">Select field...</option>
          {quizFields.map((field) => (
            <option key={field.id} value={field.id}>
              {field.label}
            </option>
          ))}
        </select>
      </div>

      {filter.field && fieldOptions.length > 0 && (
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-dark-muted block mb-1.5">
            Values (select one or more)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {fieldOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  const newValues = filter.values.includes(option.value)
                    ? filter.values.filter(v => v !== option.value)
                    : [...filter.values, option.value];
                  onValuesChange(newValues);
                }}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  filter.values.includes(option.value)
                    ? 'bg-primary-500 text-white'
                    : 'bg-dark-border text-dark-muted hover:bg-dark-border/70'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onRemove}
        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors mt-5"
        title="Remove filter"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function AdminDemographics() {
  const quizQuestions = useAllQuizQuestions();
  
  // URL search params for state management (bookmarkable)
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Parse initial state from URL
  const initialGroupBy = searchParams.get('groupBy') || 'location';
  const initialCity = searchParams.get('city') || 'all';
  const initialFilters = parseFilters(searchParams.get('filters') || '');
  
  // State
  const [groupBy, setGroupBy] = useState(initialGroupBy);
  const [selectedCity, setSelectedCity] = useState(initialCity);
  const [filters, setFilters] = useState<DemographicsFilter[]>(initialFilters);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  
  // Update URL when state changes
  const updateUrl = useCallback(() => {
    const params: Record<string, string> = {};
    if (groupBy && groupBy !== 'location') params.groupBy = groupBy;
    if (selectedCity && selectedCity !== 'all') params.city = selectedCity;
    const filtersStr = serializeFilters(filters);
    if (filtersStr) params.filters = filtersStr;
    setSearchParams(params);
  }, [groupBy, selectedCity, filters, setSearchParams]);
  
  useEffect(() => {
    updateUrl();
  }, [updateUrl]);

  // Fetch data
  const { data: demographicsData, isLoading, refetch } = useAdminDemographics({
    groupBy,
    city: selectedCity,
    filters: filters.filter(f => f.field && f.values.length > 0),
  });

  const data = demographicsData?.data;
  const breakdown = data?.breakdown || [];
  const quizFields = data?.quizFields || [];
  const cities = data?.cities || [];
  const cityBreakdown = data?.cityBreakdown || {};

  // Chart data with labels
  const chartData = useMemo(() => {
    return breakdown.slice(0, 15).map((item, index) => ({
      ...item,
      label: getQuizLabel(groupBy, item.value, quizQuestions),
      fill: COLORS[index % COLORS.length],
    }));
  }, [breakdown, groupBy, quizQuestions]);

  // City breakdown table data
  const cityBreakdownData = useMemo(() => {
    if (!data?.cityBreakdown || Object.keys(cityBreakdown).length === 0) return [];
    
    const allValues = new Set<string>();
    Object.values(cityBreakdown).forEach(values => {
      Object.keys(values).forEach(v => allValues.add(v));
    });
    
    return Array.from(allValues).map(value => ({
      value,
      label: getQuizLabel(groupBy, value, quizQuestions),
      ...Object.fromEntries(
        Object.entries(cityBreakdown).map(([city, values]) => [city, values[value] || 0])
      ),
    }));
  }, [cityBreakdown, groupBy, quizQuestions, data?.cityBreakdown]);

  // Filter handlers
  const handleAddFilter = () => {
    setFilters(prev => [...prev, { field: '', values: [] }]);
  };

  const handleUpdateFilterField = (index: number, field: string) => {
    setFilters(prev => prev.map((f, i) => 
      i === index ? { field, values: [] } : f
    ));
  };

  const handleUpdateFilterValues = (index: number, values: string[]) => {
    setFilters(prev => prev.map((f, i) => 
      i === index ? { ...f, values } : f
    ));
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    setSelectedCity('all');
  };

  // Other handlers
  const handleGroupByChange = (value: string) => {
    setGroupBy(value);
    setExpandedItem(null);
  };

  const handleCityChange = (value: string) => {
    setSelectedCity(value);
    setExpandedItem(null);
  };

  const handleCopyUserId = async (userId: string) => {
    await navigator.clipboard.writeText(userId);
    setCopiedId(userId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExportCSV = () => {
    if (!breakdown.length) return;
    
    const headers = ['Value', 'Count', 'Percentage'];
    const rows = breakdown.map(item => [
      getQuizLabel(groupBy, item.value, quizQuestions),
      item.count,
      item.percentage.toFixed(1) + '%',
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `demographics-${groupBy}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Find the field label
  const currentFieldLabel = quizFields.find(f => f.id === groupBy)?.label || groupBy;
  const activeFiltersCount = filters.filter(f => f.field && f.values.length > 0).length;
  const hasActiveFilters = activeFiltersCount > 0 || selectedCity !== 'all';

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">Demographics</h1>
            <p className="text-dark-muted text-lg">
              Analyze user quiz responses and breakdowns
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              disabled={!breakdown.length}
              className="btn btn-ghost"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="btn btn-ghost"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{data?.totalUsers?.toLocaleString() || 0}</p>
              <p className="text-dark-muted text-sm">
                {hasActiveFilters ? 'Filtered Users' : 'Total Users'}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{data?.usersWithQuiz?.toLocaleString() || 0}</p>
              <p className="text-dark-muted text-sm">Quiz Completed</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Layers className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{breakdown.length}</p>
              <p className="text-dark-muted text-sm">Unique Responses</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Controls Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card mb-8"
      >
        <div className="flex flex-col gap-6">
          {/* Primary Controls Row */}
          <div className="flex flex-wrap items-end gap-6">
            {/* Group By */}
            <div className="flex flex-col gap-2 min-w-[200px]">
              <label className="text-sm font-medium text-dark-muted flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Group By
              </label>
              <select
                value={groupBy}
                onChange={(e) => handleGroupByChange(e.target.value)}
                className="bg-dark-border text-white px-4 py-2.5 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none"
              >
                {quizFields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>

            {/* City Filter */}
            <div className="flex flex-col gap-2 min-w-[180px]">
              <label className="text-sm font-medium text-dark-muted flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                City
              </label>
              <select
                value={selectedCity}
                onChange={(e) => handleCityChange(e.target.value)}
                className="bg-dark-border text-white px-4 py-2.5 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none"
              >
                <option value="all">All Cities</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-dark-muted">View</label>
              <div className="flex gap-1">
                <button
                  onClick={() => setViewMode('chart')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    viewMode === 'chart'
                      ? 'bg-primary-500 text-white'
                      : 'bg-dark-border text-dark-muted hover:bg-dark-border/70'
                  }`}
                >
                  <PieChartIcon className="w-4 h-4" />
                  Chart
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    viewMode === 'table'
                      ? 'bg-primary-500 text-white'
                      : 'bg-dark-border text-dark-muted hover:bg-dark-border/70'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Table
                </button>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="border-t border-dark-border pt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-dark-muted" />
                <span className="text-sm font-medium text-dark-muted">
                  Filters {activeFiltersCount > 0 && `(${activeFiltersCount} active)`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button
                    onClick={handleClearAllFilters}
                    className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors flex items-center gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear All
                  </button>
                )}
                <button
                  onClick={handleAddFilter}
                  className="px-3 py-1.5 bg-primary-500/20 text-primary-400 rounded-lg text-xs font-medium hover:bg-primary-500/30 transition-colors flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Filter
                </button>
              </div>
            </div>

            {/* Filter Rows */}
            {filters.length > 0 ? (
              <div className="space-y-3">
                {filters.map((filter, index) => (
                  <FilterRow
                    key={index}
                    filter={filter}
                    quizFields={quizFields}
                    quizQuestions={quizQuestions}
                    onFieldChange={(field) => handleUpdateFilterField(index, field)}
                    onValuesChange={(values) => handleUpdateFilterValues(index, values)}
                    onRemove={() => handleRemoveFilter(index)}
                  />
                ))}
                {activeFiltersCount > 1 && (
                  <p className="text-xs text-dark-muted mt-2">
                    Multiple filters use AND logic: users must match all filter conditions
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-dark-muted">
                No filters applied. Click "Add Filter" to narrow down your results.
              </p>
            )}
          </div>
        </div>
      </motion.div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader />
        </div>
      ) : (
        <>
          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Chart View */}
            {viewMode === 'chart' && (
              <>
                {/* Pie Chart */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="card"
                >
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-primary-500" />
                    {currentFieldLabel} Distribution
                  </h2>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.slice(0, 8)}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="label"
                          label={({ label, percent }) => `${label.substring(0, 15)}${label.length > 15 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {chartData.slice(0, 8).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1F2937',
                            border: '1px solid #374151',
                            borderRadius: '0.5rem',
                            color: '#F9FAFB',
                          }}
                          formatter={(value: number) => [value, 'Users']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>

                {/* Bar Chart */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="card"
                >
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-cyan-500" />
                    Top Responses
                  </h2>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                        <YAxis
                          type="category"
                          dataKey="label"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          width={120}
                          tickFormatter={(value) => value.length > 18 ? value.substring(0, 18) + '...' : value}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1F2937',
                            border: '1px solid #374151',
                            borderRadius: '0.5rem',
                            color: '#F9FAFB',
                          }}
                          formatter={(value: number, _name: string, props: any) => [
                            `${value} users (${props.payload.percentage.toFixed(1)}%)`,
                            'Count',
                          ]}
                        />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {chartData.slice(0, 10).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>
              </>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="card lg:col-span-2"
              >
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary-500" />
                  {currentFieldLabel} Breakdown
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-dark-border">
                        <th className="text-left py-3 px-4 text-dark-muted text-sm font-medium">Response</th>
                        <th className="text-right py-3 px-4 text-dark-muted text-sm font-medium">Count</th>
                        <th className="text-right py-3 px-4 text-dark-muted text-sm font-medium">Percentage</th>
                        <th className="text-left py-3 px-4 text-dark-muted text-sm font-medium w-1/3">Distribution</th>
                        <th className="py-3 px-4 text-dark-muted text-sm font-medium w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.map((item, index) => (
                        <>
                          <tr
                            key={item.value}
                            className="border-b border-dark-border/50 hover:bg-dark-border/20 transition-colors cursor-pointer"
                            onClick={() => setExpandedItem(expandedItem === item.value ? null : item.value)}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                />
                                <span className="font-medium">
                                  {getQuizLabel(groupBy, item.value, quizQuestions)}
                                </span>
                              </div>
                            </td>
                            <td className="text-right py-3 px-4 font-semibold">
                              {item.count.toLocaleString()}
                            </td>
                            <td className="text-right py-3 px-4 text-dark-muted">
                              {item.percentage.toFixed(1)}%
                            </td>
                            <td className="py-3 px-4">
                              <div className="w-full h-4 bg-dark-border rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${item.percentage}%`,
                                    backgroundColor: COLORS[index % COLORS.length],
                                  }}
                                />
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {expandedItem === item.value ? (
                                <ChevronDown className="w-4 h-4 text-dark-muted" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-dark-muted" />
                              )}
                            </td>
                          </tr>
                          {expandedItem === item.value && (
                            <tr className="bg-dark-border/10">
                              <td colSpan={5} className="py-4 px-8">
                                <div className="text-sm text-dark-muted mb-2">
                                  Showing first {Math.min(item.users.length, 100)} users
                                </div>
                                <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
                                  {item.users.slice(0, 100).map((user) => (
                                    <div
                                      key={user.id}
                                      className="flex items-center gap-2 px-3 py-1.5 bg-dark-border/50 rounded-lg text-sm"
                                    >
                                      <span>
                                        {user.firstName} {user.lastName}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyUserId(user.id);
                                        }}
                                        className="text-dark-muted hover:text-white transition-colors"
                                        title="Copy user ID"
                                      >
                                        {copiedId === user.id ? (
                                          <Check className="w-3.5 h-3.5 text-green-500" />
                                        ) : (
                                          <Copy className="w-3.5 h-3.5" />
                                        )}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </div>

          {/* City Cross-Tabulation (only when not grouping by city) */}
          {groupBy !== 'location' && groupBy !== 'city' && Object.keys(cityBreakdown).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="card mb-8"
            >
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-500" />
                {currentFieldLabel} by City
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-border">
                      <th className="text-left py-3 px-4 text-dark-muted text-sm font-medium">Response</th>
                      {Object.keys(cityBreakdown).map((city) => {
                        // Calculate total users per city for percentage
                        const cityTotal = Object.values(cityBreakdown[city]).reduce((sum, count) => sum + count, 0);
                        return (
                          <th key={city} className="text-right py-3 px-4 text-dark-muted text-sm font-medium">
                            <div>{city}</div>
                            <div className="text-xs font-normal">({cityTotal} users)</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {cityBreakdownData.slice(0, 20).map((row, index) => (
                      <tr key={row.value} className="border-b border-dark-border/50 hover:bg-dark-border/20 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <span className="font-medium">{row.label}</span>
                          </div>
                        </td>
                        {Object.keys(cityBreakdown).map((city) => {
                          const count = (row as any)[city] || 0;
                          const cityTotal = Object.values(cityBreakdown[city]).reduce((sum, c) => sum + c, 0);
                          const percentage = cityTotal > 0 ? (count / cityTotal * 100) : 0;
                          return (
                            <td key={city} className="text-right py-3 px-4">
                              <div className="font-semibold">{count}</div>
                              <div className="text-xs text-dark-muted">{percentage.toFixed(1)}%</div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Empty State */}
          {breakdown.length === 0 && !isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card text-center py-16"
            >
              <BarChart3 className="w-16 h-16 text-dark-muted mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No Data Available</h3>
              <p className="text-dark-muted">
                {activeFiltersCount > 0
                  ? 'No users match the selected filters. Try adjusting your criteria.'
                  : 'No quiz responses found for this field.'}
              </p>
            </motion.div>
          )}

          {/* URL Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="text-center text-dark-muted text-sm"
          >
            <p>
              Tip: Bookmark this page to save your current filter configuration
            </p>
          </motion.div>
        </>
      )}
    </div>
  );
}
